import { Node, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useState, useEffect, useRef } from 'react';
import { getAllParentProductCategory } from '../../../../../api/productCategoriesApi';

const ParentCategoryBlockView = (props) => {
    const { node, updateAttributes } = props;
    const categories = node.attrs.categories || [];
    const dropdownRef = useRef(null)

    const [allCategories, setAllCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState(categories);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsDropdownOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await getAllParentProductCategory();
                setAllCategories(response?.data?.data || []);
            } catch (error) {
                console.error('Error fetching parent categories:', error);
            }
        };
        fetchCategories();
    }, []);

    const handleSelectCategory = (category) => {
        setSelectedCategories(prev => {
            const isSelected = prev.find(c => {return Number(c?.categoryId) === Number(category?.categoryId)});
            if (isSelected) {
                return prev.filter(c => {return Number(c.categoryId) !== Number(category.categoryId)});
            } else {
                return [...prev, category];
            }
        });
    };

    const handleApply = () => {
        updateAttributes({ categories: selectedCategories });
    };

    const renderCategoryItem = (category) => (
        <div key={category.categoryId} className='category-item' data-category={category.categoryId} data-key={category?.image?.key}>
            <a href={`/product-category/${category?.slug}`} className='parent-category-link'>
                <img src={category?.image?.url || '/default-category.png'} alt={category.name} />
                <p className='heading'>{category?.name}</p>
            </a>
        </div>
    );

    return (
        <NodeViewWrapper data-type="parentCategoryBlock">
            <div className='category-wrapper'>
                <div className='category-selector'>
                    <div className="dropdown-wrapper" ref={dropdownRef}>
                        <div className="dropdown-header" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                            <span className="title">Select Categories ({selectedCategories.length})</span>
                        </div>
                        {isDropdownOpen && (
                            <div className="dropdown-list">
                                {allCategories?.map(category => (
                                    <label key={category.categoryId} className="dropdown-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedCategories.some(c => Number(c.categoryId) === Number(category.categoryId))}
                                            onChange={() => handleSelectCategory(category)}
                                        />
                                        {category?.name}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className='save-btn' onClick={handleApply}>Apply parent category</button>
                </div>
                {categories.length > 0 && (
                    <div className='category-display'>
                        <div className='parent-category-grid-wrapper'>
                            {categories.slice(0, 4)?.map((category, index) => renderCategoryItem(category))}
                        </div>
                    </div>
                )}
            </div>
        </NodeViewWrapper>
    );
};

export const ParentCategoryBlock = Node.create({
    name: 'parentCategoryBlock',

    group: 'block',

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            categories: {
                default: [],
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: `div[data-type="${this.name}"]`,
                getAttrs: (element) => {
                    const items = element.querySelectorAll('.category-item');

                    const categories = Array.from(items).map(item => {
                        const categoryId = item.getAttribute('data-category');
                        const imageKey = item.getAttribute('data-key');

                        const linkEl = item.querySelector('a.parent-category-link');
                        const imgEl = linkEl?.querySelector('img');
                        const nameEl = linkEl?.querySelector('p.heading');

                        const href = linkEl?.getAttribute('href');
                        const slug = href
                            ? href.replace('/product-category/', '')
                            : null;

                        return {
                            categoryId,
                            name: nameEl?.textContent || '',
                            image: imgEl
                                ? {
                                    key: imageKey || null,
                                    url: imgEl.getAttribute('src'),
                                }
                                : null,
                            slug,
                        };
                    });

                    return { categories };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const categories = HTMLAttributes.categories || [];

        return [
            'div',
            { 'data-type': this.name, class: 'parent-category-block' },

            ...categories.map(category => [
                'div',
                {
                    class: 'category-item',
                    'data-category': category.categoryId,
                    'data-key': category?.image?.key || null,
                },
                [
                    'a',
                    {
                        href: category?.slug
                            ? `/product-category/${category.slug}`
                            : '#',
                        class: 'parent-category-link',
                    },
                    [
                        'img',
                        {
                            src: category?.image?.url || '/default-category.png',
                            alt: category?.name || '',
                        },
                    ],
                    [
                        'p',
                        { class: 'heading' },
                        category?.name || '',
                    ],
                ],
            ]),
        ];
    },


    addCommands() {
        return {
            setParentCategoryBlock:
                (options) =>
                    ({ commands }) => {
                        return commands.insertContent({
                            type: this.name,
                            attrs: {
                                categories: options?.categories || [],
                            },
                        });
                    },
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(ParentCategoryBlockView);
    },
});